export type PlanTier = "essential" | "pro" | "enterprise";

export type PlanCompareSectionId = "core" | "governance" | "ai" | "support";

export type PlanCardSubsection = "lead" | "plus" | "governance" | "ai" | "addons";

export interface PlanFeatureRow {
  id: string;
  label: string;
  section: PlanCompareSectionId;
  minPlan: PlanTier;
  /** Show on pricing card for this tier (inclusions only). */
  showOnCard?: PlanTier;
  cardSubsection?: PlanCardSubsection;
  /** If set, only this tier gets a check in compare (mutually exclusive tier feature). */
  exclusiveTier?: PlanTier;
  compareNote?: Partial<Record<PlanTier, string>>;
  /** Compare table shows these strings instead of check/minus icons. */
  compareValues?: Record<PlanTier, string>;
  /** Pricing card only; omitted from the compare matrix. */
  cardOnly?: boolean;
  /** Compare matrix only; omitted from pricing cards. */
  compareOnly?: boolean;
}

/** Yearly billing: minimum endpoint licenses on any paid plan. */
export const MIN_LICENSES_YEARLY = 10;
/** Monthly billing: Essential and Pro minimum at list rates. */
export const MIN_LICENSES_MONTHLY_SAAS = 10;
/** Monthly billing: Enterprise minimum at list rates. */
export const MIN_LICENSES_MONTHLY_ENTERPRISE = 50;
/** Enterprise SSO / OIDC is available from this seat count upward. */
export const MIN_LICENSES_FOR_SSO = 50;

export type PlanBillingCycle = "monthly" | "yearly";

export function minEndpointCardLabel(tier: PlanTier, billing: PlanBillingCycle): string {
  if (billing === "yearly") {
    return `Min. ${MIN_LICENSES_YEARLY} endpoint licenses (yearly plan)`;
  }
  if (tier === "enterprise") {
    return `Min. ${MIN_LICENSES_MONTHLY_ENTERPRISE} endpoints (monthly plan)`;
  }
  return `Min. ${MIN_LICENSES_MONTHLY_SAAS} endpoints (monthly plan)`;
}

export const PLAN_COMPARE_SECTIONS: { id: PlanCompareSectionId; title: string }[] = [
  { id: "core", title: "Core" },
  { id: "governance", title: "Governance and identity" },
  { id: "ai", title: "AI Intelligence" },
  { id: "support", title: "Support and deployment" },
];

export const PLAN_TIER_LABELS: Record<PlanTier, string> = {
  essential: "Essential",
  pro: "Pro",
  enterprise: "Enterprise",
};

export const PLAN_TIER_TAGLINES: Record<PlanTier, string> = {
  essential: "Baseline Compliance",
  pro: "Managed Accountability",
  enterprise: "Total Sovereignty",
};

export const ENTERPRISE_AI_CONSOLIDATED_LABELS = [
  "Advanced OCR (visual concept, image match, policy-linked search)",
  "Forensic Evidence Export",
  "Activity Intelligence Reports",
  "Behavioral anomaly and live alerting",
  "Compliance and data classification",
  "Workplace and content policy detection",
  "Visual Policy Rule Engine (unlimited custom rules)",
  "Policy Alerts Dashboard with review workflow",
  "Process and training intelligence",
  "Data Journey Tracking",
] as const;

/** Full compare matrix and card source of truth. */
export const PLAN_FEATURE_CATALOG: PlanFeatureRow[] = [
  {
    id: "compare-min-endpoints",
    label: "Minimum endpoints",
    section: "core",
    minPlan: "essential",
    compareOnly: true,
    compareValues: {
      essential: "2 (yearly) / 10 (monthly)",
      pro: "2 (yearly) / 10 (monthly)",
      enterprise: "2 (yearly) / 50 (monthly)",
    },
  },
  {
    id: "unlimited-hours",
    label: "Unlimited Recording Hours",
    section: "core",
    minPlan: "essential",
    showOnCard: "essential",
  },
  {
    id: "compare-data-retention",
    label: "Data retention",
    section: "core",
    minPlan: "essential",
    compareOnly: true,
    compareValues: {
      essential: "7 days",
      pro: "30 days",
      enterprise: "Unlimited",
    },
  },
  {
    id: "retention-7",
    label: "7-day data retention",
    section: "core",
    minPlan: "essential",
    showOnCard: "essential",
    cardOnly: true,
  },
  {
    id: "retention-30",
    label: "30-day data retention",
    section: "core",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "retention-enterprise",
    label: "Unlimited data retention",
    section: "core",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "compare-recording-quality",
    label: "Recording quality",
    section: "core",
    minPlan: "essential",
    compareOnly: true,
    compareValues: {
      essential: "720p / 2 FPS",
      pro: "High Definition",
      enterprise: "Full capture quality",
    },
  },
  {
    id: "capture-essential",
    label: "Standard Definition (720p / 2 FPS)",
    section: "core",
    minPlan: "essential",
    showOnCard: "essential",
    cardOnly: true,
  },
  {
    id: "capture-pro",
    label: "High Definition Capture (1080p / 6 FPS)",
    section: "core",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "capture-enterprise",
    label: "Full capture quality range (up to 4K / 24 FPS)",
    section: "core",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "compare-session-audio",
    label: "Session audio streaming",
    section: "core",
    minPlan: "pro",
    compareOnly: true,
    compareValues: {
      essential: "Optional add-on",
      pro: "Optional add-on",
      enterprise: "Optional add-on",
    },
  },
  {
    id: "compare-webcam-capture",
    label: "Webcam identity snapshots",
    section: "core",
    minPlan: "pro",
    compareOnly: true,
    compareValues: {
      essential: "Optional add-on",
      pro: "Optional add-on",
      enterprise: "Optional add-on",
    },
  },
  {
    id: "managed-cloud",
    label: "Managed Cloud Infrastructure",
    section: "core",
    minPlan: "essential",
    showOnCard: "essential",
  },
  {
    id: "compare-storage-included",
    label: "Storage included",
    section: "core",
    minPlan: "essential",
    compareOnly: true,
    compareValues: {
      essential: "200 GB",
      pro: "500 GB",
      enterprise: "Unlimited*",
    },
  },
  {
    id: "storage-200",
    label: "200 GB DeskReplay-managed storage included",
    section: "core",
    minPlan: "essential",
    showOnCard: "essential",
    cardOnly: true,
  },
  {
    id: "storage-500",
    label: "500 GB DeskReplay-managed storage included",
    section: "core",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "unlimited-storage",
    label: "Unlimited storage*",
    section: "core",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "byok-optional",
    label: "Optional: your own Cloudflare R2 or AWS S3 bucket",
    section: "core",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "compare-support",
    label: "Support",
    section: "support",
    minPlan: "essential",
    compareOnly: true,
    compareValues: {
      essential: "Basic",
      pro: "Standard",
      enterprise: "Dedicated",
    },
  },
  {
    id: "basic-support",
    label: "Basic support",
    section: "support",
    minPlan: "essential",
    showOnCard: "essential",
    cardOnly: true,
  },
  {
    id: "commercial-support",
    label: "Standard commercial support",
    section: "support",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "dedicated-support",
    label: "Dedicated support",
    section: "support",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
    cardOnly: true,
  },
  {
    id: "compare-cctv-plugin",
    label: "CCTV+ plugin (optional add-on)",
    section: "support",
    minPlan: "pro",
    compareOnly: true,
    compareValues: {
      essential: "Not available",
      pro: "Attach CCTV+ Pro (optional)",
      enterprise: "Attach CCTV+ Enterprise (optional)",
    },
  },
  {
    id: "session-audio-pro-optional",
    label: "Session Audio Pro module (optional)",
    section: "support",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "addons",
    cardOnly: true,
  },
  {
    id: "webcam-capture-pro-optional",
    label: "Webcam Capture Pro module (optional)",
    section: "support",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "addons",
    cardOnly: true,
  },
  {
    id: "cctv-plugin-pro-optional",
    label: "CCTV+ Pro plugin (optional)",
    section: "support",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "addons",
    cardOnly: true,
  },
  {
    id: "session-audio-enterprise-optional",
    label: "Session Audio Enterprise module (optional)",
    section: "support",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "addons",
    cardOnly: true,
  },
  {
    id: "webcam-capture-enterprise-optional",
    label: "Webcam Capture Enterprise module (optional)",
    section: "support",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "addons",
    cardOnly: true,
  },
  {
    id: "cctv-plugin-enterprise-optional",
    label: "CCTV+ Enterprise plugin (optional)",
    section: "support",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "addons",
    cardOnly: true,
  },
  {
    id: "admin-one",
    label: "One global admin",
    section: "governance",
    minPlan: "essential",
    exclusiveTier: "essential",
    showOnCard: "essential",
  },
  {
    id: "admin-pro",
    label: "One global admin plus three admins with limited rights",
    section: "governance",
    minPlan: "pro",
    exclusiveTier: "pro",
    showOnCard: "pro",
    cardSubsection: "governance",
  },
  {
    id: "audit-logs",
    label: "Custom Forensic Audit Logs (exportable admin and evidence-access audit trail)",
    section: "governance",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "governance",
  },
  {
    id: "google-archive",
    label: "Archive recordings to Google Drive (Google Workspace)",
    section: "governance",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "governance",
  },
  {
    id: "rest-api",
    label: "Full REST API access",
    section: "governance",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
  },
  {
    id: "sso",
    label: `SSO / OIDC (Okta, Microsoft Entra ID, Google): min. ${MIN_LICENSES_FOR_SSO} licenses required for this feature`,
    section: "governance",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
    compareNote: { enterprise: `Min. ${MIN_LICENSES_FOR_SSO} licenses for SSO` },
  },
  {
    id: "rbac",
    label: "Advanced RBAC (Super Admin, Supervisor, HR, IT, Auditor Roles)",
    section: "governance",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
    compareNote: {
      enterprise: "Optional per-admin evidence scope: desk sessions, CCTV, or both",
    },
  },
  {
    id: "self-hosted",
    label: "Self-Hosted Deployment",
    section: "support",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
  },
  {
    id: "retention-config",
    label: "Configurable retention policies (your schedules and legal hold)",
    section: "governance",
    minPlan: "enterprise",
    showOnCard: "enterprise",
    cardSubsection: "plus",
  },
  {
    id: "compare-ocr",
    label: "OCR",
    section: "ai",
    minPlan: "essential",
    compareOnly: true,
    compareValues: {
      essential: "-",
      pro: "Basic OCR",
      enterprise: "Advanced OCR",
    },
  },
  {
    id: "deskreplay-login",
    label: "DeskReplay-hosted login",
    section: "governance",
    minPlan: "essential",
    exclusiveTier: "essential",
  },
  {
    id: "deskreplay-login-pro",
    label: "DeskReplay-hosted login",
    section: "governance",
    minPlan: "pro",
    exclusiveTier: "pro",
  },
  {
    id: "basic-ocr",
    label: "Basic OCR",
    section: "ai",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "ai",
    cardOnly: true,
  },
  {
    id: "chat-byok",
    label: "Chat with Your Recordings (BYOK*)",
    section: "ai",
    minPlan: "pro",
    showOnCard: "pro",
    cardSubsection: "ai",
  },
  ...ENTERPRISE_AI_CONSOLIDATED_LABELS.map((label, i) => ({
    id: `enterprise-ai-${i + 1}`,
    label,
    section: "ai" as PlanCompareSectionId,
    minPlan: "enterprise" as PlanTier,
    showOnCard: "enterprise" as PlanTier,
    cardSubsection: "ai" as PlanCardSubsection,
  })),
];

export function planTierRank(tier: PlanTier): number {
  switch (tier) {
    case "pro":
      return 1;
    case "enterprise":
      return 2;
    default:
      return 0;
  }
}

export function tierIncludesFeature(row: PlanFeatureRow, tier: PlanTier): boolean {
  if (row.exclusiveTier) return row.exclusiveTier === tier;
  return planTierRank(tier) >= planTierRank(row.minPlan);
}

export function compareMatrixBySection(): {
  section: (typeof PLAN_COMPARE_SECTIONS)[number];
  rows: PlanFeatureRow[];
}[] {
  return PLAN_COMPARE_SECTIONS.map((section) => ({
    section,
    rows: PLAN_FEATURE_CATALOG.filter((r) => r.section === section.id && !r.cardOnly),
  })).filter((g) => g.rows.length > 0);
}

export interface PlanCardSection {
  subsection: PlanCardSubsection;
  labels: string[];
}

function pricingCardRowsForTier(tier: PlanTier): PlanFeatureRow[] {
  return PLAN_FEATURE_CATALOG.filter((r) => r.showOnCard === tier && !r.compareOnly);
}

/** Pricing card bullets for a tier (inclusions only). */
export function pricingCardSections(tier: PlanTier, billing: PlanBillingCycle): PlanCardSection[] {
  const rows = pricingCardRowsForTier(tier);
  const order: PlanCardSubsection[] = ["lead", "plus", "governance", "ai", "addons"];
  const sections = order
    .map((subsection) => ({
      subsection,
      labels: rows.filter((r) => (r.cardSubsection ?? "plus") === subsection).map((r) => r.label),
    }))
    .filter((s) => s.labels.length > 0);

  const minLabel = minEndpointCardLabel(tier, billing);
  if (sections[0]?.subsection === "lead") {
    sections[0] = { subsection: "lead", labels: [minLabel, ...sections[0].labels] };
  } else {
    sections.unshift({ subsection: "lead", labels: [minLabel] });
  }
  return sections;
}

/** Flat list for Essential (single column, no subsections). */
export function pricingCardFlatFeatures(tier: PlanTier, billing: PlanBillingCycle): string[] {
  return [
    minEndpointCardLabel(tier, billing),
    ...pricingCardRowsForTier(tier).map((r) => r.label),
  ];
}

/** CCTV+ Plugin add-on tiers (per camera per month). */
export interface CctvAddonTier {
  id: "cctv-pro" | "cctv-enterprise";
  name: string;
  tagline: string;
  priceUsdMonthly: number;
  priceInrMonthly: number;
  maxCameras: number | null;
  requiresPlan: string;
  features: string[];
  enterpriseOnly: boolean;
}

export const CCTV_ADDON_TIERS: CctvAddonTier[] = [
  {
    id: "cctv-pro",
    name: "CCTV+ Pro",
    tagline: "Cold path intelligence for up to 10 cameras",
    priceUsdMonthly: 15,
    priceInrMonthly: 1400,
    maxCameras: 10,
    requiresPlan: "DeskReplay Pro",
    enterpriseOnly: false,
    features: [
      "Up to 10 cameras",
      "Direct HTTP upload and R2 watch prefix ingest",
      "30-day footage retention",
      "Cold path deep indexing: OCR, visual search, scene analytics, NSFW screening",
      "AI narrative summaries (2-4 sentences per segment)",
      "CCTV gallery with day browser and segment list",
      "Visual and Dex AI search filters for CCTV footage",
      "Basic incident management (reviewed flag)",
      "Email alert delivery",
    ],
  },
  {
    id: "cctv-enterprise",
    name: "CCTV+ Enterprise",
    tagline: "Full dual-engine pipeline for unlimited cameras",
    priceUsdMonthly: 25,
    priceInrMonthly: 2500,
    maxCameras: null,
    requiresPlan: "DeskReplay Enterprise",
    enterpriseOnly: true,
    features: [
      "Unlimited cameras",
      "All CCTV+ Pro features",
      "Direct HTTP upload, R2 watch prefix, and bulk CSV camera import",
      "90-day footage retention (or custom)",
      "Hot path real-time alerts in under 2 seconds",
      "Immobility and sleeping guard detection",
      "Full incident management kanban with assignment and SLA tracking",
      "Email, webhook, and SMS alert delivery",
      "Forensic export with SHA-256 chain-of-custody ZIP",
      "Cross-camera timeline reconstruction in Dex AI",
      "CSV incident reports for client deliverables",
      "MSP multi-tenancy with per-client dashboards",
      "Frame relay live view",
    ],
  },
];

export const CCTV_ANNUAL_DISCOUNT = 0.2;

/** Shown on optional CCTV+ add-on callouts only (not core desktop recording copy). */
export const CCTV_ADDON_PLAN_NOTE =
  "CCTV+ is an optional plug-in purchased separately from desktop screen recording. It is billed per camera per month on top of your seat plan. Not available on Essential. CCTV+ Pro requires DeskReplay Pro. CCTV+ Enterprise requires DeskReplay Enterprise.";

/** Short intro for optional add-on strips on hub pages. */
export const CCTV_ADDON_SUMMARY =
  "The CCTV+ Plugin is a separate add-on for customers who also want NVR camera intelligence: real-time alerts, searchable archives, and forensic export. Desktop session recording works without it.";

/** Label for optional add-on sections (distinct from core product UI). */
export const CCTV_ADDON_SECTION_LABEL = "Optional add-on · purchased separately";

export type CctvHighlightContent = {
  summary: string;
  useCases: string[];
};

export type WebcamHighlightContent = {
  summary: string;
  useCases: string[];
};

export const CCTV_SOLUTION_IDS = new Set(["cctv-surveillance", "physical-security-msp"]);

export const PRO_PERSONA_BLURB =
  "Pro is for teams that need accountability without running their own stack: HD evidence, delegated admins, exportable audit trails, and AI search on DeskReplay-hosted SaaS.";

export const ENTERPRISE_PERSONA_BLURB =
  "Enterprise is for sovereignty and identity control: self-hosted deployment, corporate SSO, advanced RBAC, REST API, and the full AI intelligence suite.";
